import { useMemo, useState } from "react";
import { ArrowUpRight, ArrowRight, Info, Sparkles } from "lucide-react";
import { COURSE_LIST, CAREER_STAGES } from "@/data/courses";
import { COURSES } from "@/constants/testIds/landing";
import Reveal from "@/components/site/Reveal";
import CourseDetailDialog from "@/components/site/CourseDetailDialog";
import BookingDialog from "@/components/site/BookingDialog";
import InfopaketDialog from "@/components/site/InfopaketDialog";

/**
 * Karriere-Pfad Courses — Kinetic Editorial.
 * Statt abstrakter Tabs: eine Stufen-Leiste "Wo stehst du gerade?" (NRW-geprüfte
 * Zulassungen). Klick auf eine Stufe zeigt passende Kurse in logischer Reihenfolge.
 */

// Rhythmus: Spaltenbreiten + Farbvarianten wiederholen sich alle 6 Karten
const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-6", "lg:col-span-6"];
const VARIANTS = ["sand", "cream", "espresso", "cream", "sand", "cream"];

const CARD_STYLES = {
  sand: {
    box: "bg-[#EFE7DC] text-sand-800",
    kicker: "text-terra",
    sub: "text-sand-600",
    meta: "text-sand-700",
    metaBorder: "border-sand-800/12",
    price: "text-terra",
    priceLabel: "text-sand-500",
    arrow: "border-sand-800/25 text-sand-800 group-hover:bg-terra group-hover:border-terra group-hover:text-white",
    underline: "bg-terra",
  },
  cream: {
    box: "bg-white/80 text-sand-800 border border-sand-800/10",
    kicker: "text-terra",
    sub: "text-sand-600",
    meta: "text-sand-500",
    metaBorder: "border-sand-800/12",
    price: "text-terra",
    priceLabel: "text-sand-500",
    arrow: "border-sand-800/25 text-sand-800 group-hover:bg-terra group-hover:border-terra group-hover:text-white",
    underline: "bg-terra",
  },
  espresso: {
    box: "bg-espresso-deep text-cream-50",
    kicker: "text-saffron",
    sub: "text-cream-100/70",
    meta: "text-cream-100/60",
    metaBorder: "border-cream-50/15",
    price: "text-saffron",
    priceLabel: "text-cream-100/50",
    arrow: "border-cream-50/30 text-cream-50 group-hover:bg-saffron group-hover:border-saffron group-hover:text-espresso-deep",
    underline: "bg-saffron",
  },
};

function CoursePosterCard({ course, index, onOpen, onBook }) {
  const span = SPANS[index % SPANS.length];
  const s = CARD_STYLES[VARIANTS[index % VARIANTS.length]];

  return (
    <div
      data-testid={COURSES.card}
      onClick={() => onOpen(course)}
      className={`group relative col-span-1 ${span} flex flex-col p-7 md:p-9 min-h-[300px] md:min-h-[340px] cursor-pointer rounded-[1.75rem] shadow-[0_18px_44px_-28px_rgba(82,42,37,0.28)] transition-transform duration-300 hover:-translate-y-1.5 focus:outline-none ${s.box}`}
    >
      {/* Top strip: Nº + Preis */}
      <div className="flex items-start justify-between gap-4">
        <div className={`font-mono text-[11px] tracking-[0.2em] uppercase ${s.kicker}`}>
          Nº {String(index + 1).padStart(2, "0")}
        </div>
        <div className="text-right">
          <div className={`text-[9.5px] uppercase tracking-[0.28em] font-semibold ${s.priceLabel}`}>ab</div>
          <div className={`font-magazine text-[1.9rem] md:text-[2.2rem] leading-none mt-1 whitespace-nowrap ${s.price}`}>
            {course.price}
          </div>
        </div>
      </div>

      {/* Titel · Plakat */}
      <div className="mt-8 md:mt-10 flex-1 min-w-0">
        <h3 lang="de" className="font-magazine font-medium text-[1.65rem] md:text-[2.05rem] lg:text-[2.3rem] leading-[1.12] break-words [hyphens:auto]">
          <span className="relative inline">
            {course.title}
            <span className={`absolute left-0 -bottom-1.5 h-[3px] w-0 group-hover:w-full transition-[width] duration-500 ${s.underline}`} />
          </span>
        </h3>
        <p className={`mt-4 text-[13.5px] leading-snug italic max-w-md ${s.sub}`}>
          {course.subtitle}
        </p>
      </div>

      {/* Bottom strip: Meta + Arrow */}
      <div className={`mt-8 pt-5 border-t flex items-end justify-between gap-4 ${s.metaBorder}`}>
        <div className={`font-mono text-[10.5px] uppercase tracking-[0.14em] leading-[1.9] ${s.meta}`}>
          <div>{course.duration}</div>
          <div>{course.weeks} · {course.funding}</div>
        </div>
        <button
          type="button"
          aria-label={`${course.title}: Details ansehen & buchen`}
          onClick={(e) => {
            e.stopPropagation();
            onBook(course);
          }}
          data-testid={COURSES.cardCta}
          className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${s.arrow}`}
        >
          <ArrowUpRight className="h-4.5 w-4.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

export default function Courses() {
  const [stageId, setStageId] = useState("start");
  const [activeCourse, setActiveCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingCourse, setBookingCourse] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [infopaketCourse, setInfopaketCourse] = useState(null);
  const [infopaketOpen, setInfopaketOpen] = useState(false);

  const courseById = useMemo(() => {
    const map = {};
    COURSE_LIST.forEach((c) => { map[c.id] = c; });
    return map;
  }, []);

  const allStage = useMemo(() => ({
    id: "alle",
    num: "07",
    label: "Alle Kurse",
    short: `Kompletter Katalog · ${COURSE_LIST.length} Kurse`,
    requirement: "Gesamtübersicht",
    description: "Der komplette Katalog auf einen Blick – ideal für Pflegedienste und Einrichtungen, die mehrere Kurse für ihr Team buchen.",
    courseIds: [...new Set(CAREER_STAGES.flatMap((s) => s.courseIds))],
  }), []);

  const stages = useMemo(() => [...CAREER_STAGES, allStage], [allStage]);
  const stage = stages.find((s) => s.id === stageId);
  const stageCourses = stage.courseIds.map((id) => courseById[id]).filter(Boolean);

  const openDialog = (course) => {
    setActiveCourse(course);
    setDialogOpen(true);
  };
  const openBooking = (course) => {
    setDialogOpen(false);
    setBookingCourse(course);
    setBookingOpen(true);
  };

  return (
    <section id="kurse" className="relative bg-cream-50 py-28 md:py-40">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial header · asymmetric */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-6 items-end mb-14">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Dein Karriere-Pfad &middot; 2026
                </span>
              </div>
              <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
                Wo stehst du<br />
                <span className="text-terra italic">gerade?</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-sand-600 text-[15px] leading-[1.7] max-w-sm md:ml-auto md:text-right">
                Wähle deine Stufe — wir zeigen dir genau die Kurse, die zu deiner
                Qualifikation passen. Zulassungen NRW-geprüft. Start jeden Montag.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Karriere-Stufen-Leiste */}
        <Reveal delay={80}>
          <div data-testid={COURSES.tabs} className="mb-10">
            <div className="flex gap-3 overflow-x-auto pb-3 lg:pb-0 lg:grid lg:grid-cols-7 snap-x snap-mandatory [-webkit-overflow-scrolling:touch]">
              {stages.map((s, i) => {
                const active = s.id === stageId;
                const isAll = s.id === "alle";
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`career-stage-${s.id}`}
                    onClick={() => setStageId(s.id)}
                    className={`group/stage relative shrink-0 snap-start w-[220px] lg:w-auto text-left rounded-2xl border p-4 pt-5 transition-all duration-300 ${
                      active
                        ? isAll
                          ? "bg-sand-800 border-sand-800 text-cream-50 shadow-[0_14px_34px_-18px_rgba(61,56,53,0.55)]"
                          : "bg-terra border-terra text-cream-50 shadow-[0_14px_34px_-18px_rgba(194,69,30,0.55)]"
                        : isAll
                          ? "bg-cream-100 border-sand-800/30 border-dashed text-sand-800 hover:border-sand-800 hover:-translate-y-0.5"
                          : "bg-white/70 border-sand-800/15 text-sand-800 hover:border-terra/50 hover:-translate-y-0.5"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`font-mono text-[10px] tracking-[0.18em] ${active ? "text-cream-50/70" : "text-terra"}`}>
                        {isAll ? "A–Z" : s.num}
                      </span>
                      <span className={`h-px flex-1 ${active ? "bg-cream-50/30" : "bg-sand-800/10"}`} />
                      {!isAll && i < stages.length - 2 && (
                        <ArrowRight className={`h-3 w-3 ${active ? "text-cream-50/60" : "text-sand-400"}`} strokeWidth={2} />
                      )}
                    </div>
                    <div className="mt-2.5 font-poster text-[12px] uppercase tracking-[0.1em] font-semibold leading-snug">
                      {s.label}
                    </div>
                    <div className={`mt-1 text-[11px] leading-snug ${active ? "text-cream-50/75" : "text-sand-500"}`}>
                      {s.short}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* Aktive Stufe · Info-Zeile */}
        <div data-testid="career-stage-info" className="mb-8 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-sand-800 text-cream-50 px-4 h-8 text-[11px] font-bold uppercase tracking-[0.16em]">
            {stage.id === "alle" ? `Alle ${stageCourses.length} Kurse` : `Voraussetzung: ${stage.requirement}`}
          </span>
          <p className="text-sand-600 text-[14px] leading-relaxed max-w-2xl">{stage.description}</p>
        </div>

        {/* Ehrlicher NRW-Hinweis (z. B. Pflegefachassistenz) */}
        {stage.note && (
          <div data-testid="career-stage-note" className="mb-8 flex items-start gap-3.5 rounded-2xl border border-saffron/60 bg-saffron/15 p-5 max-w-3xl">
            <Info className="h-5 w-5 text-terra shrink-0 mt-0.5" strokeWidth={1.8} />
            <div>
              <p className="text-[14px] text-sand-800 leading-relaxed">{stage.note}</p>
              <a href="#kontakt" data-testid="career-stage-note-cta"
                className="mt-2.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-terra hover:text-terra-dark border-b border-terra/40 pb-0.5 transition-colors">
                Kostenlose Beratung zum Weg zur Fachkraft
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </a>
            </div>
          </div>
        )}

        {/* Kurse der Stufe · in logischer Reihenfolge */}
        <div data-testid="career-stage-courses" className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          {stageCourses.map((c, idx) => (
            <CoursePosterCard
              key={c.id}
              course={c}
              index={idx}
              onOpen={openDialog}
              onBook={openBooking}
            />
          ))}
        </div>

        {/* Editorial footnote + Quiz-Link */}
        <Reveal delay={160}>
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-sand-300/60 pt-8">
            <p className="text-[13px] text-sand-600 max-w-xl leading-relaxed">
              Klick auf eine Karte öffnet den vollständigen Kurs-Steckbrief — mit Terminen, Förderung und Direktbuchung.
            </p>
            <a
              href="#quiz"
              data-testid="career-quiz-link"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-sand-800 border-b border-terra pb-0.5 hover:text-terra transition-colors whitespace-nowrap"
            >
              <Sparkles className="h-4 w-4 text-terra" strokeWidth={2} />
              Unsicher, wo du stehst? 2-Minuten-Einstufung
              <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
            </a>
          </div>
        </Reveal>
      </div>

      <CourseDetailDialog
        course={activeCourse}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onBook={openBooking}
        onInfopaket={(c) => { setInfopaketCourse(c); setInfopaketOpen(true); }}
      />
      <BookingDialog
        course={bookingCourse}
        open={bookingOpen}
        onOpenChange={setBookingOpen}
      />
      <InfopaketDialog
        course={infopaketCourse}
        open={infopaketOpen}
        onOpenChange={setInfopaketOpen}
      />
    </section>
  );
}
